import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { AlertCircle, Eye, EyeOff, Receipt, DollarSign, Users, TrendingUp, Mail } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../App';
import { formatErrorMessage } from '../lib/helpers';

const Login = () => {
  const { login, API } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [currencies, setCurrencies] = useState({});
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');

  // Login form - now using email
  const [loginForm, setLoginForm] = useState({ email: '', password: '', rememberMe: true });
  
  // Register form - simplified to email, password, default_currency
  const [registerForm, setRegisterForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    default_currency: 'USD'
  });

  const fetchCurrencies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data);
    } catch (error) {
      console.error('Failed to fetch currencies:', error);
      // Set basic currencies as fallback
      setCurrencies({
        'USD': 'US Dollar',
        'EUR': 'Euro',
        'GBP': 'British Pound',
        'INR': 'Indian Rupee',
        'JPY': 'Japanese Yen'
      });
    }
  }, []);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginForm.email,
        password: loginForm.password,
        remember_me: loginForm.rememberMe
      });
      const { access_token, user } = response.data;
      
      login(access_token, user);
      
      // Show profile completion prompt if profile is incomplete
      if (user.profile_completion_percentage < 100 && user.is_temp_password === false) {
        toast.info('Complete your profile to get personalized insights!', { duration: 5000 });
      }
      
      // Show password change prompt if using temporary password
      if (user.is_temp_password) {
        toast.warning('Please change your temporary password in Profile Settings', { duration: 6000 });
      }
      
      toast.success('Welcome back!');
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = formatErrorMessage(err, 'Login failed. Please check your email and password.');
      setError(errorMessage);
      toast.error('Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!registerForm.email || !registerForm.password || !registerForm.confirmPassword) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (registerForm.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: registerForm.email,
        password: registerForm.password,
        default_currency: registerForm.default_currency
      });
      
      toast.success('Account created successfully! Please sign in.');
      
      // Switch to login tab and prefill email
      setLoginForm({ email: registerForm.email, password: '', rememberMe: true });
      setRegisterForm({
        email: '',
        password: '',
        confirmPassword: '',
        default_currency: 'USD'
      });
      setError('');
      
      // Switch to Sign In tab
      setActiveTab('login');
    } catch (err) {
      console.error('Register error:', err);
      const errorMessage = formatErrorMessage(err, 'Registration failed');
      setError(errorMessage);
      toast.error('Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setForgotPasswordLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      
      toast.success('If the email exists, a temporary password has been sent to your email');
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (err) {
      console.error('Forgot password error:', err);
      toast.error('Failed to process password reset request');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-3 sm:p-4">
      <div className="w-full max-w-6xl">
        {/* Mobile Header - Logo and Feature Tiles */}
        <div className="md:hidden mb-4">
          {/* Logo */}
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-2 rounded-xl">
              <Receipt className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Monyply
            </h1>
          </div>
          
          {/* 3 Feature Tiles in Single Row */}
          <div className="grid grid-cols-3 gap-2 mb-2">
            <div className="flex flex-col items-center p-2 bg-white rounded-lg shadow-sm">
              <div className="bg-teal-100 p-1.5 rounded-lg mb-1">
                <Users className="w-4 h-4 text-teal-600" />
              </div>
              <h3 className="font-semibold text-gray-900 text-[10px] text-center">Split Bills</h3>
            </div>

            <div className="flex flex-col items-center p-2 bg-white rounded-lg shadow-sm">
              <div className="bg-emerald-100 p-1.5 rounded-lg mb-1">
                <Receipt className="w-4 h-4 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-gray-900 text-[10px] text-center">Track Expenses</h3>
            </div>

            <div className="flex flex-col items-center p-2 bg-white rounded-lg shadow-sm">
              <div className="bg-cyan-100 p-1.5 rounded-lg mb-1">
                <TrendingUp className="w-4 h-4 text-cyan-600" />
              </div>
              <h3 className="font-semibold text-gray-900 text-[10px] text-center">Budget Track</h3>
            </div>
          </div>
          
          {/* Tagline - Mobile only */}
          <p className="text-center text-xs text-gray-500 mt-2">
            Start managing your expenses smarter today!
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Left side - Branding (Desktop Only) */}
          <div className="hidden md:block space-y-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-3 rounded-2xl">
                  <Receipt className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  Monyply
                </h1>
              </div>
              <p className="text-xl text-gray-600 font-medium">Smart expense management made simple</p>
            </div>

            <div className="space-y-4 mt-12">
              <div className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-sm">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <Receipt className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Track Expenses</h3>
                  <p className="text-sm text-gray-600">Manage all your expenses in one place</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-sm">
                <div className="bg-teal-100 p-2 rounded-lg">
                  <Users className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Split Bills</h3>
                  <p className="text-sm text-gray-600">Easily split expenses with friends and groups</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-white rounded-lg shadow-sm">
                <div className="bg-cyan-100 p-2 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Budget Track</h3>
                  <p className="text-sm text-gray-600">Track spending and stay within budget</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Auth Form */}
          <Card className="w-full shadow-xl">
            <CardHeader className="pb-2 sm:pb-6">
              <CardTitle className="text-xl sm:text-2xl">Get Started</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Join thousands managing expenses smarter!
              </CardDescription>
            </CardHeader>
            <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Sign In</TabsTrigger>
                <TabsTrigger value="register">Sign Up</TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="remember-me"
                        checked={loginForm.rememberMe}
                        onChange={(e) => setLoginForm({ ...loginForm, rememberMe: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                      <Label htmlFor="remember-me" className="text-sm text-gray-600 cursor-pointer select-none">
                        Remember me for 30 days
                      </Label>
                    </div>
                    <Button
                      type="button"
                      variant="link"
                      className="text-sm text-emerald-600 hover:text-emerald-700 p-0"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot Password?
                    </Button>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                    disabled={loading}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email *</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-currency">Default Currency *</Label>
                    <Select
                      value={registerForm.default_currency}
                      onValueChange={(value) => setRegisterForm({ ...registerForm, default_currency: value })}
                      disabled={loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(currencies).map(([code, name]) => (
                          <SelectItem key={code} value={code}>
                            {code} - {name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password *</Label>
                    <div className="relative">
                      <Input
                        id="register-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="At least 6 characters"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        required
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-confirm-password">Confirm Password *</Label>
                    <Input
                      id="register-confirm-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Re-enter your password"
                      value={registerForm.confirmPassword}
                      onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                    disabled={loading}
                  >
                    {loading ? 'Creating account...' : 'Sign Up'}
                  </Button>

                  <p className="text-xs text-gray-500 text-center mt-2">
                    You can complete your profile after signing up (optional)
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Forgot Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we will send you a temporary password
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email Address</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="your.email@example.com"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                disabled={forgotPasswordLoading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowForgotPassword(false)}
              disabled={forgotPasswordLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleForgotPassword}
              disabled={forgotPasswordLoading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {forgotPasswordLoading ? 'Sending...' : 'Send Temporary Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer - Hidden on mobile since tagline is shown above */}
      <div className="hidden sm:block absolute bottom-4 left-1/2 -translate-x-1/2 text-center text-sm text-gray-500">
        Start managing your expenses smarter today!
      </div>
    </div>
  );
};

export default Login;
